/**
 * ShareProduct — baris tombol bagikan di modal detail produk.
 *
 * Deretan inline, bukan dropdown: body modal punya `overflow-y-auto`, jadi
 * popover absolute akan terpotong di layar pendek. Lagi pula ini menghemat
 * satu tap.
 */

import { useCallback, useState } from "react";
import { MonoText } from "./CopyButton";
import { productShareUrl, shareIntentUrl, type ShareTarget } from "@/lib/share";

const TARGETS: { id: ShareTarget; label: string; icon: string }[] = [
  { id: "whatsapp", label: "WhatsApp", icon: "fa-brands fa-whatsapp" },
  { id: "x", label: "X", icon: "fa-brands fa-x-twitter" },
  { id: "facebook", label: "Facebook", icon: "fa-brands fa-facebook-f" },
  { id: "threads", label: "Threads", icon: "fa-brands fa-threads" },
];

const ITEM_CLASS =
  "inline-flex items-center gap-1.5 font-mono text-2xs tracking-widest uppercase " +
  "border px-2 py-1 border-border-strong text-ink-muted " +
  "hover:border-terminal hover:text-terminal";

const ITEM_CLASS_ACTIVE =
  "inline-flex items-center gap-1.5 font-mono text-2xs tracking-widest uppercase " +
  "border px-2 py-1 border-terminal text-terminal";

export function ShareProduct({ slug, name }: { slug: string; name: string }) {
  const [copied, setCopied] = useState(false);

  const url = productShareUrl(slug, window.location.origin);
  const text = `${name} — AG5R.STORE`;

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API butuh secure context dan bisa ditolak user. Kalau gagal,
      // jangan diam — tampilkan URL-nya supaya tetap bisa disalin manual.
      window.prompt("Salin link produk:", url);
    }
  }, [url]);

  return (
    <div className="mt-4">
      <div className="terminal-label mb-2">
        <i className="fa-solid fa-share-nodes mr-1.5" aria-hidden />
        BAGIKAN PRODUK
      </div>
      <div className="flex flex-wrap gap-1.5">
        {TARGETS.map((t) => (
          <a
            key={t.id}
            href={shareIntentUrl(t.id, url, text)}
            target="_blank"
            // noreferrer wajib berpasangan dengan noopener: tanpa itu halaman
            // sharer memegang window.opener ke tab toko.
            rel="noopener noreferrer"
            title={`Bagikan ke ${t.label}`}
            aria-label={`Bagikan ke ${t.label}`}
            className={ITEM_CLASS}
          >
            <i className={t.icon} aria-hidden />
            <span className="hidden sm:inline">{t.label}</span>
          </a>
        ))}
        <button
          type="button"
          onClick={copyLink}
          title="Salin link produk"
          aria-label="Salin link produk"
          className={copied ? ITEM_CLASS_ACTIVE : ITEM_CLASS}
        >
          <i className={copied ? "fa-solid fa-check" : "fa-solid fa-link"} aria-hidden />
          <span className="hidden sm:inline">{copied ? "Tersalin" : "Salin Link"}</span>
        </button>
      </div>
      <MonoText size="xs" color="ink-dim" className="block mt-1.5 break-all">
        {url}
      </MonoText>
    </div>
  );
}
